import { resolveMediaUrl } from '../../../utils/media';
import { buildDisplayableMessageText } from '../../../utils/messageStatus';
import { showWarning } from '../../../utils/toast';
import { onlyDigits } from '../../../utils/phone';
import { getConversationContact } from '../../../utils/conversationContact.js';

export const MIN_INITIAL_MESSAGES = 10;

export const formatDayLabel = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return 'Sem data';
  }
  const label = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getInitialVisibleDayCount = (groups, minMessages = MIN_INITIAL_MESSAGES) => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return 0;
  }

  let messageCounter = 0;
  let dayCounter = 0;

  for (let index = groups.length - 1; index >= 0; index -= 1) {
    dayCounter += 1;
    messageCounter += Array.isArray(groups[index]?.messages)
      ? groups[index].messages.length
      : 0;

    if (messageCounter >= minMessages) {
      break;
    }
  }

  return dayCounter;
};

export const pickFirstFilled = (...values) => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
};

export const getPrimaryContact = (entry) => {
  if (!entry) return null;
  const contacts = Array.isArray(entry.contacts)
    ? entry.contacts.filter(Boolean)
    : [];
  if (contacts.length > 0) {
    return contacts[0];
  }
  return getConversationContact(entry) || entry.Contact_cliente || null;
};

export const resolveChatAvatarUrl = (chatData) => {
  const primaryContact = getPrimaryContact(chatData);
  const candidate = pickFirstFilled(
    chatData?.photo,
    chatData?.photograph,
    chatData?.client_photo,
    chatData?.customer_photo,
    chatData?.contact_photo,
    chatData?.contact?.photograph,
    chatData?.contact?.photo,
    primaryContact?.photograph,
    primaryContact?.photo
  );
  if (!candidate) return null;
  const resolved = resolveMediaUrl(candidate);
  return resolved || null;
};

export const getMessageKey = (message, fallbackIndex, groupKey = 'group') => {
  if (message && typeof message === 'object') {
    if (message.id !== null && message.id !== undefined) {
      if (typeof message.id === 'string' && message.id.startsWith('lifecycle:')) {
        return message.id;
      }
      return `id-${message.id}`;
    }
    if (message.message_id !== null && message.message_id !== undefined) {
      if (typeof message.message_id === 'string' && message.message_id.startsWith('lifecycle:')) {
        return message.message_id;
      }
      return `message-${message.message_id}`;
    }
    if (message.messageId !== null && message.messageId !== undefined) {
      return `message-${message.messageId}`;
    }
    if (message.baileys_message_id) {
      return `baileys-${message.baileys_message_id}`;
    }
    const ts = message.timestamp || message.created_at || message.updated_at;
    if (ts) {
      return `ts-${ts}-${groupKey}-${fallbackIndex}`;
    }
  }
  return `idx-${groupKey}-${fallbackIndex}`;
};

export const ADMIN_COMMENTS_LAST_VIEWED_KEY = 'chat_admin_comments_last_viewed';

export const readAdminCommentsLastViewedMap = () => {
  try {
    const raw = localStorage.getItem(ADMIN_COMMENTS_LAST_VIEWED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const writeAdminCommentsLastViewedMap = (map) => {
  try {
    localStorage.setItem(
      ADMIN_COMMENTS_LAST_VIEWED_KEY,
      JSON.stringify(map && typeof map === 'object' ? map : {})
    );
  } catch {
    // ignore storage errors
  }
};

export const extractShortcutParts = (value = '') => {
  if (typeof value !== 'string') {
    return { prefix: '', query: null };
  }
  const lastIndex = value.lastIndexOf('\\');
  if (lastIndex === -1) {
    return { prefix: value, query: null };
  }
  // se houver uma sequência de barras invertidas consecutivas, trate toda
  // a sequência como o marcador do atalho (por exemplo, `\\`), removendo
  // todas elas do prefixo.
  let startIndex = lastIndex;
  while (startIndex > 0 && value[startIndex - 1] === '\\') {
    startIndex -= 1;
  }
  const after = value.slice(lastIndex + 1);
  const newlineIndex = after.search(/\r?\n/);
  const query = newlineIndex === -1 ? after : after.slice(0, newlineIndex);
  return {
    prefix: value.slice(0, startIndex),
    query,
  };
};

export const stripShortcutMarker = (value = '') => {
  const { prefix, query } = extractShortcutParts(value);
  if (query === null) return value;
  return prefix.replace(/\s+$/, '');
};

const safeParseJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const extractPhonesFromVcard = (vcard) => {
  if (typeof vcard !== 'string') return [];
  const entries = [];
  const lines = vcard.split(/\r?\n/);
  lines.forEach((line) => {
    if (typeof line !== 'string') return;
    const trimmed = line.trim();
    if (!trimmed) return;
    const waidMatch = trimmed.match(/waid=([\d]+)/i);
    if (waidMatch && waidMatch[1]) {
      entries.push(waidMatch[1]);
    }
    const isTelLine = /^tel/i.test(trimmed) || trimmed.toLowerCase().includes('tel;');
    if (!isTelLine) return;
    const colonIndex = trimmed.indexOf(':');
    if (colonIndex === -1) return;
    const afterColon = trimmed.slice(colonIndex + 1).trim();
    if (afterColon) {
      entries.push(afterColon);
    }
  });
  return entries.filter(Boolean);
};

const coerceString = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str || null;
};

const pushCandidate = (bucket, value) => {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    value.forEach((v) => pushCandidate(bucket, v));
    return;
  }
  const coerced = coerceString(value);
  if (coerced) {
    bucket.push(coerced);
  }
};

export const resolveSharedContactData = (message) => {
  if (!message || typeof message !== 'object') return null;

  const shared = typeof message.shared_contact === 'object' && message.shared_contact !== null
    ? message.shared_contact
    : {};
  const payload = safeParseJson(
    shared.payload ??
      message.shared_contact_payload ??
      message.sharedContactPayload ??
      message.contact_payload
  );
  const messagePayload = safeParseJson(message.message_payload ?? message.messagePayload);

  let contactMessage = null;
  if (messagePayload && typeof messagePayload === 'object') {
    contactMessage =
      messagePayload.contactMessage ||
      messagePayload.contactsArrayMessage ||
      messagePayload.message?.contactMessage ||
      null;
  }

  const contactEntry =
    contactMessage && Array.isArray(contactMessage.contacts) && contactMessage.contacts.length > 0
      ? contactMessage.contacts[0]
      : contactMessage;

  const vcard =
    coerceString(shared.vcard) ||
    coerceString(message.shared_contact_vcard) ||
    coerceString(payload?.vcard) ||
    coerceString(payload?.contact?.vcard) ||
    coerceString(contactEntry?.vcard) ||
    null;

  const phoneCandidates = [];
  const displayPhoneCandidates = [];
  const pushPhone = (value) => pushCandidate(phoneCandidates, value);
  const pushDisplayPhone = (value) => pushCandidate(displayPhoneCandidates, value);

  pushDisplayPhone(payload?.raw_phone);
  pushDisplayPhone(payload?.rawPhone);
  pushDisplayPhone(payload?.contact?.raw_phone);
  pushDisplayPhone(shared.phone);
  pushDisplayPhone(message.shared_contact_phone);
  pushDisplayPhone(payload?.phone);
  pushDisplayPhone(payload?.contact?.phone);
  pushDisplayPhone(payload?.phones);
  pushDisplayPhone(payload?.contact?.phones);
  pushDisplayPhone(contactEntry?.phone);
  pushDisplayPhone(contactEntry?.phones);
  pushDisplayPhone(extractPhonesFromVcard(vcard));

  pushPhone(shared.phone);
  pushPhone(message.shared_contact_phone);
  pushPhone(payload?.phone);
  pushPhone(payload?.raw_phone);
  pushPhone(payload?.rawPhone);
  pushPhone(payload?.contact?.phone);
  pushPhone(payload?.contact?.raw_phone);
  pushPhone(payload?.phones);
  pushPhone(payload?.contact?.phones);
  pushPhone(contactEntry?.phone);
  pushPhone(contactEntry?.phones);
  pushPhone(extractPhonesFromVcard(vcard));

  const name =
    coerceString(shared.name) ||
    coerceString(message.shared_contact_name) ||
    coerceString(payload?.name) ||
    coerceString(payload?.contact?.name) ||
    coerceString(payload?.displayName) ||
    coerceString(payload?.contact?.displayName) ||
    coerceString(contactEntry?.displayName) ||
    null;

  const email =
    coerceString(shared.email) ||
    coerceString(message.shared_contact_email) ||
    coerceString(payload?.email) ||
    coerceString(payload?.contact?.email) ||
    null;

  const company =
    coerceString(shared.company) ||
    coerceString(message.shared_contact_company) ||
    coerceString(payload?.company) ||
    coerceString(payload?.contact?.company) ||
    null;

  const phoneDigits = phoneCandidates.map(onlyDigits).find(Boolean) || null;
  const rawPhone =
    displayPhoneCandidates.find((entry) => entry && entry.trim()) ||
    phoneCandidates.find((entry) => entry && entry.trim()) ||
    null;

  const resolvedPhone = phoneDigits || rawPhone;

  if (!name && !resolvedPhone && !email && !company) {
    return null;
  }

  return {
    name,
    phone: resolvedPhone || '',
    rawPhone: rawPhone || null,
    email,
    company,
    vcard,
  };
};

export const buildMessagePreviewSnippet = (message) => {
  if (!message) return '';
  const sharedContact = resolveSharedContactData(message);
  if (sharedContact) {
    const phone = sharedContact.phone || sharedContact.rawPhone;
    const suffix = phone ? ` (${phone})` : '';
    return `Contato: ${sharedContact.name || 'Contato'}${suffix}`;
  }
  const raw = typeof message.message === 'string' ? message.message : '';
  const { combined } = buildDisplayableMessageText(raw);
  if (combined && combined.trim()) {
    return combined.trim();
  }
  const type = (message.type || '').toLowerCase();
  const labelMap = {
    image: 'Imagem',
    video: 'Vídeo',
    audio: 'Áudio',
    document: 'Documento',
    sticker: 'Figurinha',
    contact: 'Contato',
    reaction: 'Reação',
  };
  const label = labelMap[type] || 'Mensagem';
  if (message.file_name) {
    return `[${label}: ${message.file_name}]`;
  }
  return `[${label}]`;
};

export const formatFileSize = (size = 0) => {
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
export const MAX_FILE_SIZE = 48 * 1024 * 1024;

export const isImageFile = (file) => file?.type?.startsWith?.('image/');

export const validateFileSize = (file) => {
  if (!file || !Number.isFinite(file.size)) return false;
  const limit = isImageFile(file) ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
  if (file.size <= limit) return true;
  showWarning(
    isImageFile(file)
      ? 'Imagens só podem ter até 5 MB.'
      : 'Arquivos só podem ter até 48 MB.'
  );
  return false;
};
